import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Header from "../components/Header";
import CuppingGuideWidget from "../components/CuppingGuideWidget";
import ScaAffectiveGuideWidget from "../components/ScaAffectiveGuideWidget";
import { apiFetch } from "../api";
import "./SessionRecording.css";

const SessionRecording = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [session, setSession] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [recordingTime, setRecordingTime] = useState(0);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [browserSupported, setBrowserSupported] = useState(true);
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia("(max-width: 768px)").matches;
  });
  const [showFloatingRecordButton, setShowFloatingRecordButton] =
    useState(false);
  const [protocols, setProtocols] = useState([]);
  const [selectedProtocolId, setSelectedProtocolId] = useState("");

  const selectedProtocol = protocols.find((p) => p.id === selectedProtocolId);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const recordButtonAnchorRef = useRef(null);

  useEffect(() => {
    // Check browser support for mediaDevices
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setBrowserSupported(false);
      const isHTTPS = window.location.protocol === "https:";
      const isLocalhost =
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1";

      if (!isHTTPS && !isLocalhost) {
        setError(
          `⚠️ Microphone access requires HTTPS or localhost. Current URL: ${window.location.protocol}//${window.location.hostname}. Please use https:// or localhost.`,
        );
      } else {
        setError(
          "⚠️ Your browser doesn't support audio recording. Please use a modern browser like Chrome, Firefox, or Safari.",
        );
      }
    }
  }, []);

  useEffect(() => {
    if (sessionId) {
      fetchSession();
      fetchEntries();
    } else {
      createNewSession();
    }

    return () => {
      // Cleanup
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [sessionId]);

  useEffect(() => {
    fetchProtocols();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(max-width: 768px)");
    const handleViewportChange = (event) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleViewportChange);
      return () =>
        mediaQuery.removeEventListener("change", handleViewportChange);
    }

    mediaQuery.addListener(handleViewportChange);
    return () => mediaQuery.removeListener(handleViewportChange);
  }, []);

  useEffect(() => {
    if (!isMobileViewport) {
      setShowFloatingRecordButton(false);
      return undefined;
    }

    const anchor = recordButtonAnchorRef.current;

    if (!anchor) {
      return undefined;
    }

    if (typeof window !== "undefined" && "IntersectionObserver" in window) {
      const observer = new window.IntersectionObserver(
        ([entry]) => {
          setShowFloatingRecordButton(!entry.isIntersecting);
        },
        {
          threshold: 0,
        },
      );

      observer.observe(anchor);

      return () => observer.disconnect();
    }

    const updateFloatingButtonVisibility = () => {
      const { top, bottom } = anchor.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      const isVisible = bottom > 0 && top < viewportHeight;

      setShowFloatingRecordButton(!isVisible);
    };

    updateFloatingButtonVisibility();
    window.addEventListener("scroll", updateFloatingButtonVisibility, {
      passive: true,
    });
    window.addEventListener("resize", updateFloatingButtonVisibility);

    return () => {
      window.removeEventListener("scroll", updateFloatingButtonVisibility);
      window.removeEventListener("resize", updateFloatingButtonVisibility);
    };
  }, [isMobileViewport]);

  const createNewSession = async () => {
    try {
      const response = await apiFetch("/sessions", {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to create session");
      }

      const newSession = await response.json();
      navigate(`/sessions/${newSession.id}/record`, { replace: true });
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchSession = async () => {
    try {
      const response = await apiFetch(`/sessions/${sessionId}`);

      if (!response.ok) {
        throw new Error("Failed to fetch session");
      }

      const data = await response.json();
      setSession(data);

      // Redirect if session is not in recording status
      if (data.status !== "recording") {
        navigate("/sessions");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const fetchEntries = async () => {
    try {
      const response = await apiFetch(`/sessions/${sessionId}/entries`);

      if (!response.ok) {
        throw new Error("Failed to fetch entries");
      }

      const data = await response.json();
      setEntries(data.entries || []);
    } catch (err) {
      console.error("Error fetching entries:", err);
    }
  };

  const fetchProtocols = async () => {
    try {
      const response = await apiFetch("/cupping-protocols");
      if (response.ok) {
        const data = await response.json();
        setProtocols(data);
        const defaultProtocol = data.find((p) => p.name === "cupdat Standard");
        if (defaultProtocol) {
          setSelectedProtocolId(defaultProtocol.id);
        } else if (data.length > 0) {
          setSelectedProtocolId(data[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to fetch protocols:", err);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const startRecording = async () => {
    if (!selectedProtocolId) {
      setError("Please select a cupping protocol first");
      return;
    }

    try {
      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          "Microphone access not available. Please use HTTPS or a supported browser.",
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm;codecs=opus",
      });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        chunksRef.current.push(event.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        await uploadRecording(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setError("");
      setPermissionDenied(false);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime((t) => {
          if (t >= 300) {
            // 5 minutes
            stopRecording();
            setError("Recording stopped: 5 minute limit reached");
            return t;
          }
          return t + 1;
        });
      }, 1000);
    } catch (err) {
      console.error("Error accessing microphone:", err);

      if (err.name === "NotAllowedError") {
        setPermissionDenied(true);
        setError(
          "Microphone access denied. Click 'Allow Microphone' below or enable it in your browser settings.",
        );
      } else if (err.name === "NotFoundError") {
        setPermissionDenied(false);
        setError(
          "No microphone found. Please connect a microphone and try again.",
        );
      } else if (err.message && err.message.includes("HTTPS")) {
        setPermissionDenied(false);
        setError(
          "Microphone requires HTTPS. Please access this site via https:// or ensure you're on localhost.",
        );
      } else {
        setPermissionDenied(false);
        setError(
          `Microphone error: ${err.message || "Browser may not support audio recording. Try Chrome, Firefox, or Safari on HTTPS."}`,
        );
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const uploadRecording = async (audioBlob) => {
    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", audioBlob, "recording.webm");
      formData.append("protocol_id", selectedProtocolId);

      const response = await apiFetch(`/sessions/${sessionId}/entries`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Upload failed");
      }

      const entry = await response.json();
      setEntries((prev) => [...prev, entry]);
    } catch (err) {
      console.error("Upload error:", err);
      setError(`Upload error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm("Delete this recording?")) {
      return;
    }

    try {
      const response = await apiFetch(
        `/sessions/${sessionId}/entries/${entryId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete entry");
      }

      setEntries((prev) => prev.filter((e) => e.id !== entryId));
    } catch (err) {
      setError(err.message);
    }
  };

  const processSession = async () => {
    if (entries.length === 0) {
      setError("Please record at least one note before processing");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await apiFetch(`/sessions/${sessionId}/identify`, {
        method: "POST",
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to process session");
      }

      navigate(`/sessions/${sessionId}/confirm`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const cancelSession = async () => {
    if (
      !window.confirm(
        "Are you sure you want to cancel this session? All recordings will be deleted.",
      )
    ) {
      return;
    }

    try {
      await apiFetch(`/sessions/${sessionId}`, {
        method: "DELETE",
      });

      navigate("/sessions");
    } catch (err) {
      setError(err.message);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const formatTimestamp = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const renderRecordButton = (variant = "primary") => {
    const isFloating = variant === "floating";

    return (
      <button
        className={`record-button ${isRecording ? "recording" : ""} ${
          isFloating ? "record-button-floating" : ""
        }`}
        onClick={isRecording ? stopRecording : startRecording}
        disabled={uploading || !browserSupported}
        aria-label={isRecording ? "Stop recording" : "Start recording"}
      >
        <div className="record-icon">{isRecording ? "⏹" : "🎤"}</div>
        <div className="record-text">{isRecording ? "STOP" : "RECORD"}</div>
        {isRecording && (
          <>
            <div className="recording-pulse" />
            <div className="recording-time">{formatTime(recordingTime)}</div>
          </>
        )}
      </button>
    );
  };

  if (!sessionId) {
    return (
      <div className="dashboard-container">
        <div className="loading">Creating session...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <Header user={user} onLogout={handleLogout} />

      <main className="dashboard-content recording-content">
        <div className="session-header">
          <h2>Cupping Session</h2>
          {session && (
            <p className="session-started">
              Started: {new Date(session.created_at).toLocaleString()}
            </p>
          )}
        </div>

        {error && <div className="error-message">{error}</div>}

        <div className="record-section">
          <div className="protocol-selector">
            <label htmlFor="protocol-select">Protocol:</label>
            <select
              id="protocol-select"
              value={selectedProtocolId}
              onChange={(e) => setSelectedProtocolId(e.target.value)}
              className="protocol-select"
            >
              {protocols.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
          <div ref={recordButtonAnchorRef} className="record-button-anchor">
            {renderRecordButton()}
          </div>

          {permissionDenied && browserSupported && (
            <button
              className="btn-retry-permission"
              onClick={startRecording}
              disabled={uploading || isRecording}
            >
              🔒 Allow Microphone
            </button>
          )}
        </div>

        {uploading && (
          <div className="uploading-indicator">
            <div className="spinner"></div>
            <p>Transcribing audio...</p>
          </div>
        )}

        {isMobileViewport &&
          showFloatingRecordButton &&
          renderRecordButton("floating")}

        {selectedProtocol?.name === "SCA Affective Assessment" ? (
          <ScaAffectiveGuideWidget />
        ) : (
          <CuppingGuideWidget />
        )}

        <div className="entries-section">
          <h3>Recordings ({entries.length})</h3>

          {entries.length === 0 ? (
            <p className="no-entries">
              No recordings yet. Tap the record button to add your first note.
            </p>
          ) : (
            <div className="entries-list">
              {entries.map((entry) => (
                <div key={entry.id} className="entry-card">
                  <div className="entry-header">
                    <span className="entry-time">
                      {formatTimestamp(entry.created_at)}
                    </span>
                  </div>
                  <p className="entry-transcript">
                    {entry.raw_transcript.substring(0, 150)}
                    {entry.raw_transcript.length > 150 && "..."}
                  </p>
                  <div className="entry-actions">
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="btn-delete-entry"
                    >
                      🗑 Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="session-actions">
          <button onClick={cancelSession} className="btn-cancel">
            Cancel Session
          </button>
          <button
            onClick={processSession}
            disabled={entries.length === 0 || loading}
            className="btn-process"
          >
            {loading ? "Processing..." : "Process Session"}
          </button>
        </div>
      </main>

      {loading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="spinner large"></div>
            <p>Identifying batches...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SessionRecording;
